import { Button, Grid, Stack, Typography } from '@mui/material';
import { FunctionComponent, useState } from 'react';
import AssetPropertyDraggable from './AssetPropertyDraggable';
import { v4 as uuid } from 'uuid';
import { AssetProperty } from '../../../types';
import { Add } from '@mui/icons-material';
import AddAssetPropertyDialog from '../add-asset-property/AddAssetPropertyDialog';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';

interface IAssetPropertiesListProps {
  assetProperties: AssetProperty[];
  onChange: (assetProperties: AssetProperty[]) => void;
  testId: string;
  disabled?: boolean;
}

const AssetPropertiesList: FunctionComponent<IAssetPropertiesListProps> = ({
  assetProperties,
  onChange,
  testId,
  disabled,
}) => {
  const [openAssetPropertyColumn, setOpenAssetPropertyColumn] = useState(false);
  const [assetProperty, setAssetProperty] = useState<AssetProperty | null>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const sensors = useSensors(useSensor(PointerSensor));

  return (
    <Stack gap="8px">
      <Typography variant="body1">Asset Properties</Typography>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => {
          const { active, over } = event;
          if (active.id !== over?.id) {
            const oldIndex = assetProperties.findIndex((p) => p.id === active.id);
            const newIndex = assetProperties.findIndex((p) => p.id === over?.id);
            const newItems = arrayMove(assetProperties, oldIndex, newIndex);
            const orderedItems = newItems.map((item, index) => ({
              ...item,
              order: index,
            }));
            onChange(orderedItems);
          }
        }}
      >
        <SortableContext items={assetProperties.map((p) => p.id)} strategy={rectSortingStrategy}>
          <Grid container spacing={2}>
            {assetProperties.map((property) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={property.id}>
                <AssetPropertyDraggable
                  item={property}
                  disabled={disabled}
                  onEdit={(data) => {
                    setAssetProperty(data);
                    setOpenAssetPropertyColumn(true);
                    setIsEdit(true);
                  }}
                  onRemove={(data) => {
                    const updated = assetProperties
                      .filter((asset) => asset.id !== data.id)
                      .map((item, index) => ({
                        ...item,
                        order: index,
                      }));
                    onChange(updated);
                  }}
                  key={property.id}
                />
              </Grid>
            ))}
          </Grid>
        </SortableContext>
      </DndContext>
      <Stack flexDirection="row" justifyContent="flex-end">
        <Button
          onClick={() => {
            setOpenAssetPropertyColumn(true);
          }}
          variant="text"
          color="primary"
          startIcon={<Add />}
          data-sm={testId}
          disabled={disabled}
        >
          Add column
        </Button>
      </Stack>

      <AddAssetPropertyDialog
        assetPropertieslist={assetProperties}
        onClose={() => {
          setAssetProperty(null);
          setOpenAssetPropertyColumn(false);
        }}
        onCancel={() => {
          setAssetProperty(null);
          setOpenAssetPropertyColumn(false);
        }}
        assetProperty={assetProperty}
        onSubmit={(data) => {
          setAssetProperty(null);
          let newArray: AssetProperty[];

          const newAssetProperty: AssetProperty = {
            id: uuid(),
            asset: {
              type: data.asset.type,
              property: {
                available: true,
                value: (data.asset.property as string).split('_')[0],
                label: (data.asset.property as string).split('_')[1],
              },
            },
            aggregation: {
              value: (data.aggregation as string).split('_')[0],
              label: (data.aggregation as string).split('_')[1],
            },
            columnStatistics: {
              TOTAL: (data.TOTAL as boolean) ?? false,
              MIN: (data.MIN as boolean) ?? false,
              MAX: (data.MAX as boolean) ?? false,
              AVERAGE: (data.AVERAGE as boolean) ?? false,
            },
            order: 0,
          };
          if (isEdit && assetProperty) {
            newArray = assetProperties.map((col) =>
              col.id === assetProperty.id ? { ...newAssetProperty, id: assetProperty.id } : col
            );
          } else {
            newArray = [...assetProperties, newAssetProperty];
          }
          newArray = newArray.map((item, index) => ({
            ...item,
            order: index,
          }));
          onChange(newArray);
          setOpenAssetPropertyColumn(false);
          setIsEdit(false);
        }}
        open={openAssetPropertyColumn}
      />
    </Stack>
  );
};

export default AssetPropertiesList;
